import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';

// Import actions from the new Redux slices
import { setUISettings } from 'reducers/slices/uisettings';
import { setLanguageSettings } from 'reducers/slices/languagesettings';
import { setAudioSettings } from 'reducers/slices/audiosettings';
import { setColorSettings } from 'reducers/slices/colorsettings';
import { setQuizSettings } from 'reducers/slices/quizsettings';
import { setSkillSettings } from 'reducers/slices/skillsettings';
import { setDateSettings } from 'reducers/slices/datesettings';
import { saveAll as setCollapseSettings } from 'reducers/slices/collapsesettings';
const SettingsContext = /*#__PURE__*/createContext(null);
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
export const SettingsProvider = ({
  children
}) => {
  const dispatch = useDispatch();

  // Get settings from each slice
  const uisettings = useSelector(state => state.uisettings);
  const languagesettings = useSelector(state => state.languagesettings);
  const audiosettings = useSelector(state => state.audiosettings);
  const colorsettings = useSelector(state => state.colorsettings);
  const quizsettings = useSelector(state => state.quizsettings);
  const skillsettings = useSelector(state => state.skillsettings);
  const datesettings = useSelector(state => state.datesettings);
  const collapsesettings = useSelector(state => state.collapsesettings);

  // Combine all settings for backward compatibility
  const combinedSettings = {
    ...uisettings,
    ...languagesettings,
    ...audiosettings,
    ...colorsettings,
    ...quizsettings,
    ...skillsettings,
    ...datesettings,
    ...collapsesettings
  };

  // Local state for optimistic updates
  const [settings, setSettings] = useState(combinedSettings);

  // Map setting keys to their respective slices
  const settingSliceMap = {
    // UI settings
    darkMode: 'uisettings',
    showOutline: 'uisettings',
    showCharacter: 'uisettings',
    fontFamily: 'uisettings',
    charSize: 'uisettings',
    pinyinSize: 'uisettings',
    charSpaceVal: 'uisettings',
    // Language settings
    traditional: 'languagesettings',
    showPinyin: 'languagesettings',
    showRead: 'languagesettings',
    audioDownload: 'languagesettings',
    // Audio settings
    speed: 'audiosettings',
    volume: 'audiosettings',
    // Color settings
    charColors: 'colorsettings',
    pinyinColors: 'colorsettings',
    globalDash: 'colorsettings',
    lightFirstTone: 'colorsettings',
    lightSecondTone: 'colorsettings',
    lightThirdTone: 'colorsettings',
    lightFourthTone: 'colorsettings',
    lightFifthTone: 'colorsettings',
    darkFirstTone: 'colorsettings',
    darkSecondTone: 'colorsettings',
    darkThirdTone: 'colorsettings',
    darkFourthTone: 'colorsettings',
    darkFifthTone: 'colorsettings',
    // Quiz settings
    frontWord: 'quizsettings',
    frontDefinition: 'quizsettings',
    frontPinyin: 'quizsettings',
    frontAudio: 'quizsettings',
    backWord: 'quizsettings',
    backDefinition: 'quizsettings',
    backPinyin: 'quizsettings',
    backAudio: 'quizsettings',
    wordSelection: 'quizsettings',
    limitSelection: 'quizsettings',
    memLimitSelection: 'quizsettings',
    showHintAfterMiss: 'quizsettings',
    sentenceSelection: 'quizsettings',
    // Skill settings
    skill: 'skillsettings',
    cats: 'skillsettings',
    skillSelected: 'skillsettings',
    // Date settings
    startDate: 'datesettings',
    endDate: 'datesettings',
    // Collapse settings
    dictCollapse: 'collapsesettings',
    sentenceCollapse: 'collapsesettings',
    rulesCollapse: 'collapsesettings'
  };

  // Get the appropriate action for a given slice
  const getActionForSlice = sliceName => {
    switch (sliceName) {
      case 'uisettings':
        return setUISettings;
      case 'languagesettings':
        return setLanguageSettings;
      case 'audiosettings':
        return setAudioSettings;
      case 'colorsettings':
        return setColorSettings;
      case 'quizsettings':
        return setQuizSettings;
      case 'skillsettings':
        return setSkillSettings;
      case 'datesettings':
        return setDateSettings;
      case 'collapsesettings':
        return setCollapseSettings;
      default:
        return null;
    }
  };

  // Group settings by slice
  const groupSettingsBySlice = settings => {
    const grouped = {};
    Object.keys(settings).forEach(key => {
      const sliceName = settingSliceMap[key];
      if (sliceName) {
        if (!grouped[sliceName]) {
          grouped[sliceName] = {};
        }
        grouped[sliceName][key] = settings[key];
      }
    });
    return grouped;
  };

  // Debounced function to update Redux state
  const debouncedUpdateRedux = useCallback(debounce(newSettings => {
    const groupedSettings = groupSettingsBySlice(newSettings);
    Object.keys(groupedSettings).forEach(sliceName => {
      const action = getActionForSlice(sliceName);
      if (action) {
        dispatch(action(groupedSettings[sliceName]));
      }
    });
  }, 500), [dispatch]);

  // Update a single setting
  const updateSetting = useCallback((key, value) => {
    setSettings(prevSettings => {
      const newSettings = {
        ...prevSettings,
        [key]: value
      };

      // Find which slice this setting belongs to
      const sliceName = settingSliceMap[key];
      if (sliceName) {
        const action = getActionForSlice(sliceName);
        if (action) {
          // Dispatch immediately for single settings
          dispatch(action({
            [key]: value
          }));
        }
      } else {
        // If we don't know which slice it belongs to, use the debounced update
        debouncedUpdateRedux({
          [key]: value
        });
      }
      return newSettings;
    });
  }, [dispatch, debouncedUpdateRedux]);

  // Bulk update multiple settings
  const updateSettings = useCallback(newSettingsObj => {
    setSettings(prevSettings => {
      const newSettings = {
        ...prevSettings,
        ...newSettingsObj
      };
      debouncedUpdateRedux(newSettingsObj);
      return newSettings;
    });
  }, [debouncedUpdateRedux]);

  // Keep local state in sync with Redux state
  useEffect(() => {
    setSettings(combinedSettings);
  }, [uisettings, languagesettings, audiosettings, colorsettings, quizsettings, skillsettings, datesettings, collapsesettings]);
  return /*#__PURE__*/React.createElement(SettingsContext.Provider, {
    value: {
      settings,
      updateSetting,
      updateSettings,
      // Expose individual settings objects for components
      // that want to be more specific
      uisettings,
      languagesettings,
      audiosettings,
      colorsettings,
      quizsettings,
      skillsettings,
      datesettings,
      collapsesettings
    }
  }, children);
};